/**
 * Google Analytics, loaded only after the reader accepts analytics cookies.
 *
 * CONSENT_SCRIPT runs in <head> before first paint (see gatsby-ssr.js). With
 * a saved "granted" choice it loads GA right away; with no saved choice it
 * sets data-consent-pending on <html>, which shows the consent banner. With
 * no GA tracking ID in the build, it isn't added, so the banner never shows.
 */
import { readStorage, writeStorage } from "./storage";

export const CONSENT_KEY = "kbAnalyticsConsent"; // "granted" | "denied"
export const PENDING_ATTR = "data-consent-pending";

const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";
const OPEN_EVENT = "kb:open-consent";

// The banner asks only about analytics, so ad features stay off: no Google
// signals, no ad personalization, and ad consent denied for Consent Mode.
const AD_CONSENT = {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
};
const CONFIG = {
    send_page_view: false, // trackPageView() sends them, on every route change
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
};

/** A GA4 measurement ID, such as G-AB12CD34EF. */
export const isMeasurementId = id => /^G-[A-Z0-9]+$/.test(id || "");

/** The build's GA tracking ID, or undefined when analytics is off. */
export const trackingId = () =>
    typeof window !== "undefined" ? window.KB_GA_ID : undefined;

export const readConsent = () => {
    const choice = readStorage(CONSENT_KEY);
    return choice === "granted" || choice === "denied" ? choice : null;
};

/** Saves the reader's choice and starts or stops analytics to match. */
export function saveConsent(choice) {
    writeStorage(CONSENT_KEY, choice);
    if (choice === "granted") {
        window[`ga-disable-${trackingId()}`] = false;
        loadAnalytics();
        trackPageView();
    } else {
        stopAnalytics();
    }
}

function loadAnalytics() {
    if (window.gtag) return;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };
    window.gtag("consent", "default", { ...AD_CONSENT, analytics_storage: "granted" });
    window.gtag("js", new Date());
    window.gtag("config", trackingId(), CONFIG);
    const script = document.createElement("script");
    script.async = true;
    script.src = `${GTAG_SRC}?id=${encodeURIComponent(trackingId())}`;
    document.head.appendChild(script);
}

/** Stops GA sending anything and removes the cookies it set. */
function stopAnalytics() {
    window[`ga-disable-${trackingId()}`] = true;
    const host = window.location.hostname;
    const domains = ["", host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
    document.cookie
        .split(";")
        .map(cookie => cookie.split("=")[0].trim())
        .filter(name => name === "_ga" || name.startsWith("_ga_") || name === "_gid")
        .forEach(name =>
            domains.forEach(domain => {
                document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
            })
        );
}

/**
 * Sends a page view for the current page, if analytics is on. Waits briefly,
 * so the new page's <title> is in place after client-side navigation.
 */
export function trackPageView() {
    if (!window.gtag || readConsent() !== "granted") return;
    window.setTimeout(() => {
        window.gtag("event", "page_view", {
            page_location: window.location.href,
            page_title: document.title,
        });
    }, 50);
}

/** Shows the consent banner again, from Cookie settings in the footer. */
export function openConsentBanner(opener) {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { opener } }));
}

export function onOpenConsentBanner(handler) {
    const listener = event => handler(event.detail && event.detail.opener);
    window.addEventListener(OPEN_EVENT, listener);
    return () => window.removeEventListener(OPEN_EVENT, listener);
}

// The same logic as readConsent and loadAnalytics, written out because it
// runs before any bundle loads.
export const consentScript = id => `(function () {
  window.KB_GA_ID = ${JSON.stringify(id)};
  var choice;
  try {
    choice = localStorage.getItem("${CONSENT_KEY}");
  } catch (e) {
    return;
  }
  if (choice !== "granted" && choice !== "denied") {
    document.documentElement.setAttribute("${PENDING_ATTR}", "");
  } else if (choice === "granted") {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { dataLayer.push(arguments); };
    gtag("consent", "default", ${JSON.stringify({ ...AD_CONSENT, analytics_storage: "granted" })});
    gtag("js", new Date());
    gtag("config", window.KB_GA_ID, ${JSON.stringify(CONFIG)});
    var script = document.createElement("script");
    script.async = true;
    script.src = "${GTAG_SRC}?id=" + encodeURIComponent(window.KB_GA_ID);
    document.head.appendChild(script);
  }
})();`;
